import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Play,
    RotateCcw,
    SkipBack,
    SkipForward,
    ChevronLeft,
    ChevronRight,
    AlertTriangle,
    Eye,
    Maximize2,
    Plus,
    Info,
} from "lucide-react";
import PalletVisualizer from "../../components/PalletVisualizer/PalletVisualizer";
import Rail from "../../components/primitives/Rail";
import ParamGroup from "../../components/primitives/ParamGroup";
import NumericField from "../../components/primitives/NumericField";
import Stat from "../../components/primitives/Stat";
import StatusPill from "../../components/primitives/StatusPill";
import KeyChip from "../../components/primitives/KeyChip";
import IconButton from "../../components/primitives/IconButton";
import TextButton from "../../components/primitives/TextButton";
import TopStatusStrip from "../../components/primitives/TopStatusStrip";
import SegmentedControl from "../../components/primitives/SegmentedControl";
import { packMulti, ApiError } from "../../services/api";
import { colorByBoxId } from "../../utils/colorByBoxId";
import ManifestRow from "./ManifestRow";
import SkuPickerModal from "./SkuPickerModal";
import "../../styles/viz-shell.css";
import "./ManifestPage.css";

const STATUS_META = {
    idle: { label: "Idle", tone: "idle", pulse: false },
    packing: { label: "Packing…", tone: "working", pulse: true },
    ready: { label: "Fitted", tone: "success", pulse: false },
    stale: { label: "Stale", tone: "working", pulse: false },
    error: { label: "Error", tone: "danger", pulse: false },
};

const MAX_TOTAL_ITEMS = 200;
const MAX_ROWS = 100;

function makeJobId() {
    const n = Math.floor(Math.random() * 9000) + 1000;
    return `MA-${n}`;
}

function formatElapsed(ms) {
    if (ms == null) return "—";
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
}

function num(v) {
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
}

function emptyRow(id) {
    return { id, name: "", w: "", h: "", d: "", qty: "" };
}

function defaultRows() {
    return [
        { id: "row-1", name: "Cereal box", w: 10, h: 4, d: 6, qty: 12 },
        { id: "row-2", name: "Soup can", w: 3, h: 3, d: 5, qty: 50 },
        { id: "row-3", name: "Detergent", w: 8, h: 5, d: 12, qty: "" },
    ];
}

function validateRow(row, binDims) {
    const w = num(row.w), h = num(row.h), d = num(row.d);
    if (!Number.isFinite(w) || !Number.isFinite(h) || !Number.isFinite(d)) {
        return { dim: true, msg: "Width, height, and depth are required." };
    }
    if (w <= 0 || h <= 0 || d <= 0) {
        return { dim: true, msg: "Dimensions must be greater than zero." };
    }
    const bs = [num(binDims.W), num(binDims.H), num(binDims.D)].sort((a, b) => a - b);
    const rs = [w, h, d].sort((a, b) => a - b);
    if (rs[0] > bs[0] || rs[1] > bs[1] || rs[2] > bs[2]) {
        return { fit: true, msg: "Won't fit any orientation of the bin." };
    }
    if (row.qty !== "" && row.qty != null) {
        const q = num(row.qty);
        if (!Number.isFinite(q) || q <= 0 || !Number.isInteger(q)) {
            return { dim: true, msg: "Quantity must be a positive whole number, or blank." };
        }
    }
    return null;
}

function predictedRowCount(row, binVol) {
    const q = num(row.qty);
    if (Number.isFinite(q) && q > 0) return Math.floor(q);
    const w = num(row.w), h = num(row.h), d = num(row.d);
    const v = w * h * d;
    if (!Number.isFinite(v) || v <= 0 || !Number.isFinite(binVol)) return 0;
    return Math.floor(binVol / v);
}

function rowsEqual(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const x = a[i], y = b[i];
        if (
            x.id !== y.id ||
            x.name !== y.name ||
            String(x.w) !== String(y.w) ||
            String(x.h) !== String(y.h) ||
            String(x.d) !== String(y.d) ||
            String(x.qty) !== String(y.qty)
        ) {
            return false;
        }
    }
    return true;
}

function ManifestPage() {
    const [binW, setBinW] = useState(24);
    const [binH, setBinH] = useState(18);
    const [binD, setBinD] = useState(24);

    const [rows, setRows] = useState(defaultRows);
    const nextIdRef = useRef(rows.length + 1);

    const [response, setResponse] = useState(null);
    const [view, setView] = useState("volume");
    const [displayCount, setDisplayCount] = useState(0);
    const [packStatus, setPackStatus] = useState("idle");
    const [errMsg, setErrMsg] = useState(null);
    const [jobId, setJobId] = useState(makeJobId);
    const [elapsed, setElapsed] = useState(null);
    const [packedSnapshot, setPackedSnapshot] = useState(null);
    const [showWarnDetails, setShowWarnDetails] = useState(false);
    const [pickerForIdx, setPickerForIdx] = useState(null);

    const startRef = useRef(null);
    const controlsRef = useRef(null);
    const abortRef = useRef(null);
    const resetView = () => controlsRef.current?.reset?.();

    const binVol = useMemo(
        () => num(binW) * num(binH) * num(binD),
        [binW, binH, binD]
    );

    const binDims = useMemo(() => ({ W: binW, H: binH, D: binD }), [binW, binH, binD]);

    const containerObj = useMemo(
        () => ({ width: num(binW), height: num(binH), depth: num(binD) }),
        [binW, binH, binD]
    );

    const rowErrors = useMemo(
        () => rows.map((r) => validateRow(r, binDims)),
        [rows, binDims]
    );

    const totalItems = useMemo(() => {
        return rows.reduce((s, r) => s + predictedRowCount(r, binVol), 0);
    }, [rows, binVol]);

    const hasRowErrors = rowErrors.some((e) => e);
    const overItems = totalItems > MAX_TOTAL_ITEMS;
    const overRows = rows.length > MAX_ROWS;
    const noRows = rows.length === 0;
    const canPack =
        packStatus !== "packing" &&
        !noRows &&
        !hasRowErrors &&
        !overItems &&
        !overRows &&
        Number.isFinite(binVol) && binVol > 0;

    const chosen = response
        ? view === "count"
            ? response.by_count
            : response.by_volume
        : null;

    const placements = useMemo(
        () =>
            chosen?.fitted?.map((p) => ({
                position: p.position,
                dimensions: p.dimensions,
                rotation_type: p.rotation_type,
                color: colorByBoxId(p.box_id),
            })) ?? [],
        [chosen]
    );

    const totalBoxes = chosen?.fitted?.length ?? 0;
    const utilization = chosen?.totals?.utilization;
    const fittedCount = chosen?.totals?.fitted_count;
    const requestedCount = chosen?.totals?.requested_count;
    const filledVol = chosen?.totals?.filled_volume;

    const isStale =
        !!response &&
        !!packedSnapshot &&
        packStatus === "ready" &&
        (num(binW) !== packedSnapshot.W ||
            num(binH) !== packedSnapshot.H ||
            num(binD) !== packedSnapshot.D ||
            !rowsEqual(rows, packedSnapshot.rows));

    const handlePack = async () => {
        if (!canPack) return;
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setPackStatus("packing");
        setResponse(null);
        setDisplayCount(0);
        setErrMsg(null);
        setElapsed(null);
        setJobId(makeJobId());
        startRef.current = performance.now();

        const payload = {
            bin: { width: num(binW), height: num(binH), depth: num(binD) },
            boxes: rows.map((r) => {
                const box = {
                    id: r.id,
                    width: num(r.w),
                    height: num(r.h),
                    depth: num(r.d),
                };
                if (r.name) box.name = r.name;
                const q = num(r.qty);
                if (Number.isFinite(q) && q > 0) box.quantity = Math.floor(q);
                return box;
            }),
        };

        try {
            const result = await packMulti(payload, { signal: controller.signal });
            setResponse(result);
            setDisplayCount(0);
            setPackedSnapshot({
                W: num(binW),
                H: num(binH),
                D: num(binD),
                rows: rows.map((r) => ({ ...r })),
            });
            setElapsed(performance.now() - startRef.current);
            setPackStatus("ready");
        } catch (err) {
            if (err?.name === "AbortError") return;
            const msg =
                err instanceof ApiError
                    ? err.message
                    : err?.message || "Pack request failed";
            setErrMsg(msg);
            setElapsed(performance.now() - startRef.current);
            setPackStatus("error");
        }
    };

    const handleReset = () => {
        if (abortRef.current) abortRef.current.abort();
        setResponse(null);
        setDisplayCount(0);
        setPackStatus("idle");
        setErrMsg(null);
        setElapsed(null);
        setPackedSnapshot(null);
    };

    const updateRow = (idx, field, value) => {
        setRows((rs) => {
            const next = rs.slice();
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });
    };

    const addRow = () => {
        if (rows.length >= MAX_ROWS) return;
        setRows((rs) => {
            const id = `row-${nextIdRef.current++}`;
            return [...rs, emptyRow(id)];
        });
    };

    const removeRow = (idx) => {
        setRows((rs) => rs.filter((_, i) => i !== idx));
    };

    const applySku = (idx, sku) => {
        setRows((rs) =>
            rs.map((r, i) =>
                i === idx
                    ? {
                          ...r,
                          name: sku.name ?? r.name,
                          w: sku.width,
                          h: sku.height,
                          d: sku.depth,
                          sku: {
                              id: sku.id,
                              name: sku.name,
                              sku_code: sku.sku_code ?? null,
                              w: sku.width,
                              h: sku.height,
                              d: sku.depth,
                          },
                      }
                    : r
            )
        );
    };

    const unlinkSku = (idx) => {
        setRows((rs) =>
            rs.map((r, i) => (i === idx ? { ...r, sku: undefined } : r))
        );
    };

    const stepFirst = () => setDisplayCount(0);
    const stepLast = () => setDisplayCount(totalBoxes);
    const stepNext = () => setDisplayCount((c) => Math.min(c + 1, totalBoxes));
    const stepPrev = () => setDisplayCount((c) => Math.max(c - 1, 0));

    const handlersRef = useRef({});
    handlersRef.current = {
        handlePack,
        handleReset,
        stepNext,
        stepPrev,
        stepFirst,
        stepLast,
        resetView,
        setView,
    };

    useEffect(() => {
        const onKey = (e) => {
            const t = e.target;
            const inField =
                t &&
                (t.tagName === "INPUT" ||
                    t.tagName === "TEXTAREA" ||
                    t.tagName === "SELECT" ||
                    t.isContentEditable);
            const h = handlersRef.current;
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                h.handlePack();
                return;
            }
            if (inField) return;
            if (e.key === "ArrowRight") {
                e.preventDefault();
                h.stepNext();
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                h.stepPrev();
            } else if (e.key === "Home") {
                e.preventDefault();
                h.stepFirst();
            } else if (e.key === "End") {
                e.preventDefault();
                h.stepLast();
            } else if (e.key.toLowerCase() === "r" && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                h.handleReset();
            } else if (e.key.toLowerCase() === "v" && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                h.resetView();
            } else if (e.key === "1") {
                e.preventDefault();
                h.setView("volume");
            } else if (e.key === "2") {
                e.preventDefault();
                h.setView("count");
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, []);

    const meta = isStale ? STATUS_META.stale : STATUS_META[packStatus];
    const packLabel =
        packStatus === "packing" ? "Packing" : isStale ? "Re-pack" : "Pack";

    const submitErrors = [];
    if (noRows) submitErrors.push("Add at least one box type to the manifest.");
    if (overRows) submitErrors.push(`Manifest exceeds ${MAX_ROWS} rows.`);
    if (hasRowErrors) submitErrors.push("Fix the highlighted rows before packing.");
    if (overItems)
        submitErrors.push(
            `Predicted total ${totalItems} items exceeds the ${MAX_TOTAL_ITEMS} cap. Cap an unbounded row's quantity.`
        );

    const summary = chosen?.summary ?? [];

    return (
        <div className="viz-page">
            <TopStatusStrip
                left={
                    <>
                        <span className="uppercase-label">Job</span>
                        <span className="mono viz-job-id">#{jobId}</span>
                    </>
                }
                center={
                    <StatusPill tone={meta.tone} pulse={meta.pulse}>
                        {meta.label}
                    </StatusPill>
                }
                right={
                    <>
                        <span className="uppercase-label">Elapsed</span>
                        <span className="mono-num">{formatElapsed(elapsed)}</span>
                    </>
                }
            />

            <div className="viz-page__body">
                <Rail side="left" label="Manifest">
                    <div className="viz-rail__scroll">
                        <ParamGroup title="Container Dimensions">
                            <NumericField label="Width" value={binW} onChange={setBinW} />
                            <NumericField label="Height" value={binH} onChange={setBinH} />
                            <NumericField label="Depth" value={binD} onChange={setBinD} />
                        </ParamGroup>

                        <section className="manifest">
                            <header className="manifest__header">
                                <span className="uppercase-label">
                                    Manifest · {rows.length}{" "}
                                    {rows.length === 1 ? "type" : "types"}
                                </span>
                                <IconButton
                                    icon={Plus}
                                    label="Add row"
                                    size="sm"
                                    onClick={addRow}
                                    disabled={rows.length >= MAX_ROWS}
                                />
                            </header>

                            <div className="manifest__rows">
                                {rows.map((row, idx) => (
                                    <ManifestRow
                                        key={row.id}
                                        row={row}
                                        swatch={colorByBoxId(row.id)}
                                        errors={rowErrors[idx]}
                                        canRemove={rows.length > 1}
                                        onChange={(field, value) =>
                                            updateRow(idx, field, value)
                                        }
                                        onRemove={() => removeRow(idx)}
                                        onOpenPicker={() => setPickerForIdx(idx)}
                                        onUnlinkSku={() => unlinkSku(idx)}
                                    />
                                ))}
                            </div>

                            <footer
                                className={`manifest__footer ${overItems ? "manifest__footer--warn" : ""}`}
                            >
                                <span className="uppercase-label">Total items</span>
                                <span className="mono-num">
                                    {totalItems}
                                    <span className="manifest__cap">/{MAX_TOTAL_ITEMS}</span>
                                </span>
                            </footer>
                        </section>
                    </div>

                    <div className="viz-rail__cta">
                        {errMsg && (
                            <div className="viz-error" role="alert">
                                <AlertTriangle size={14} aria-hidden="true" />
                                <span>{errMsg}</span>
                            </div>
                        )}
                        {!errMsg && submitErrors.length > 0 && packStatus !== "packing" && (
                            <div className="manifest-warn" role="status">
                                <div className="manifest-warn__head">
                                    <AlertTriangle
                                        size={14}
                                        aria-hidden="true"
                                        className="manifest-warn__icon"
                                    />
                                    <span className="manifest-warn__msg">
                                        {submitErrors[0]}
                                    </span>
                                    {overItems && (
                                        <button
                                            type="button"
                                            className="manifest-warn__info focus-ring"
                                            onClick={() =>
                                                setShowWarnDetails((s) => !s)
                                            }
                                            aria-expanded={showWarnDetails}
                                            aria-label={
                                                showWarnDetails
                                                    ? "Hide details"
                                                    : "Show details"
                                            }
                                            title="Why this warning?"
                                        >
                                            <Info size={14} aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                                {overItems && showWarnDetails && (
                                    <div className="manifest-warn__details">
                                        <div className="manifest-warn__detail-head">
                                            <span className="uppercase-label">
                                                Bin volume
                                            </span>
                                            <span className="mono-num">
                                                {Number.isFinite(binVol)
                                                    ? binVol.toFixed(0)
                                                    : "—"}
                                                <span className="manifest-warn__unit">
                                                    in³
                                                </span>
                                            </span>
                                        </div>
                                        <ul className="manifest-warn__breakdown">
                                            {rows.map((r) => {
                                                const w = num(r.w);
                                                const h = num(r.h);
                                                const d = num(r.d);
                                                const v = w * h * d;
                                                const q = num(r.qty);
                                                const isBounded =
                                                    Number.isFinite(q) && q > 0;
                                                const valid =
                                                    Number.isFinite(v) && v > 0;
                                                const count = isBounded
                                                    ? Math.floor(q)
                                                    : valid
                                                        ? Math.floor(binVol / v)
                                                        : 0;
                                                return (
                                                    <li
                                                        className="manifest-warn__row"
                                                        key={r.id}
                                                    >
                                                        <span
                                                            className="manifest-warn__swatch"
                                                            style={{
                                                                background: colorByBoxId(
                                                                    r.id
                                                                ),
                                                            }}
                                                            aria-hidden="true"
                                                        />
                                                        <span className="manifest-warn__name">
                                                            {r.name || r.id}
                                                        </span>
                                                        <span className="manifest-warn__formula mono-num">
                                                            {isBounded
                                                                ? count
                                                                : valid
                                                                    ? `⌊${binVol.toFixed(0)}÷${v.toFixed(0)}⌋ = ${count}`
                                                                    : "—"}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        <div className="manifest-warn__total">
                                            <span className="uppercase-label">
                                                Total
                                            </span>
                                            <span className="mono-num">
                                                {totalItems}
                                                <span className="manifest-warn__cap">
                                                    /{MAX_TOTAL_ITEMS}
                                                </span>
                                            </span>
                                        </div>
                                        <p className="manifest-warn__note">
                                            Rows with Q = — are seeded by volume ratio
                                            (the most that could theoretically fit by
                                            volume alone). The {MAX_TOTAL_ITEMS} cap keeps
                                            the solver bounded — set a quantity on an
                                            unbounded row to lower the predicted total.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="viz-rail__cta-row">
                            <TextButton
                                variant="primary"
                                icon={Play}
                                fullWidth
                                loading={packStatus === "packing"}
                                onClick={handlePack}
                                disabled={!canPack}
                            >
                                {packLabel}
                            </TextButton>
                            <TextButton
                                variant="ghost"
                                icon={RotateCcw}
                                onClick={handleReset}
                                disabled={packStatus === "idle" && !response}
                                aria-label="Reset"
                            >
                                Reset
                            </TextButton>
                        </div>
                        <div className="viz-rail__cta-hint mono">
                            <KeyChip>Cmd + Enter</KeyChip>
                            <span>Run pack</span>
                        </div>
                    </div>
                </Rail>

                <section className="viz-canvas" aria-label="3D viewport">
                    {response && (
                        <button
                            type="button"
                            className="viz-canvas__reset-view focus-ring"
                            onClick={resetView}
                            aria-label="Reset view"
                            title="Reset view (V)"
                        >
                            <Maximize2 size={14} aria-hidden="true" />
                            <span>Reset view</span>
                        </button>
                    )}
                    {response ? (
                        <PalletVisualizer
                            container={containerObj}
                            placements={placements}
                            bestOrientation={chosen?.best_orientation}
                            displayCount={displayCount}
                            controlsRef={controlsRef}
                        />
                    ) : (
                        <div className="viz-empty" role="status">
                            <div className="viz-empty__corners" aria-hidden="true">
                                <span className="viz-empty__corner viz-empty__corner--tl" />
                                <span className="viz-empty__corner viz-empty__corner--tr" />
                                <span className="viz-empty__corner viz-empty__corner--bl" />
                                <span className="viz-empty__corner viz-empty__corner--br" />
                            </div>
                            <div className="viz-empty__inner">
                                <div className="uppercase-label viz-empty__eyebrow">
                                    {packStatus === "error"
                                        ? "Run failed"
                                        : "Awaiting manifest"}
                                </div>
                                <h2 className="viz-empty__title">
                                    {packStatus === "error"
                                        ? "Pack request did not return a solution."
                                        : "Define a manifest to begin."}
                                </h2>
                                <p className="viz-empty__lede">
                                    {packStatus === "error"
                                        ? "Check the manifest in the left rail and re-run."
                                        : "Add box types in the left rail, then run Pack."}
                                </p>
                            </div>
                        </div>
                    )}
                </section>

                <Rail side="right" label="Result">
                    <div className="viz-rail__scroll">
                        <div className="viz-section">
                            <div className="uppercase-label viz-section__label">
                                Optimise for
                            </div>
                            <SegmentedControl
                                ariaLabel="Solution to display"
                                value={view}
                                onChange={setView}
                                options={[
                                    { value: "volume", label: "Volume" },
                                    { value: "count", label: "Count" },
                                ]}
                            />
                        </div>

                        <div className="viz-section">
                            <div className="uppercase-label viz-section__label">
                                Result
                            </div>
                            <div className="viz-stats">
                                <Stat
                                    label="Utilization"
                                    value={
                                        utilization != null
                                            ? (utilization * 100).toFixed(1)
                                            : "—"
                                    }
                                    unit={utilization != null ? "%" : undefined}
                                    tone="accent"
                                    size="lg"
                                />
                                <Stat
                                    label="Fitted"
                                    value={
                                        fittedCount != null
                                            ? requestedCount != null
                                                ? `${fittedCount} / ${requestedCount}`
                                                : `${fittedCount}`
                                            : "—"
                                    }
                                    hint={
                                        requestedCount == null && fittedCount != null
                                            ? "no cap"
                                            : undefined
                                    }
                                    size="lg"
                                />
                            </div>
                        </div>

                        {summary.length > 0 && (
                            <div className="viz-section">
                                <div className="uppercase-label viz-section__label">
                                    Summary
                                </div>
                                <div className="manifest-summary">
                                    {summary.map((s) => (
                                        <div className="manifest-summary__row" key={s.box_id}>
                                            <span
                                                className="manifest-summary__swatch"
                                                style={{ background: colorByBoxId(s.box_id) }}
                                                aria-hidden="true"
                                            />
                                            <span
                                                className="manifest-summary__name"
                                                title={s.name || s.box_id}
                                            >
                                                {s.name || s.box_id}
                                            </span>
                                            <span className="manifest-summary__count mono-num">
                                                {s.fitted}
                                                <span className="manifest-summary__sep">/</span>
                                                {s.requested == null ? "∞" : s.requested}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="viz-section">
                            <div className="uppercase-label viz-section__label">
                                Stepping
                            </div>
                            <Stat
                                label="Showing"
                                value={`${displayCount} / ${totalBoxes}`}
                                size="md"
                            />
                            <TextButton
                                icon={Eye}
                                fullWidth
                                onClick={stepLast}
                                disabled={!response || displayCount >= totalBoxes}
                            >
                                Preview solution
                            </TextButton>
                            <div
                                className="viz-stepper"
                                role="group"
                                aria-label="Step through boxes"
                            >
                                <IconButton
                                    icon={SkipBack}
                                    label="First box"
                                    size="md"
                                    onClick={stepFirst}
                                    disabled={!response || displayCount === 0}
                                />
                                <IconButton
                                    icon={ChevronLeft}
                                    label="Previous box"
                                    size="md"
                                    onClick={stepPrev}
                                    disabled={!response || displayCount === 0}
                                />
                                <IconButton
                                    icon={ChevronRight}
                                    label="Next box"
                                    size="md"
                                    onClick={stepNext}
                                    disabled={!response || displayCount >= totalBoxes}
                                />
                                <IconButton
                                    icon={SkipForward}
                                    label="Last box"
                                    size="md"
                                    onClick={stepLast}
                                    disabled={!response || displayCount >= totalBoxes}
                                />
                            </div>
                        </div>

                        <div className="viz-section">
                            <div className="uppercase-label viz-section__label">
                                Solver
                            </div>
                            <div className="viz-rows">
                                <div className="viz-row">
                                    <span className="uppercase-label">Best orientation</span>
                                    <span className="mono viz-row__val">
                                        {chosen?.best_orientation || "—"}
                                    </span>
                                </div>
                                <div className="viz-row">
                                    <span className="uppercase-label">Container vol</span>
                                    <span className="mono-num viz-row__val">
                                        {Number.isFinite(binVol) ? binVol.toFixed(0) : "—"}
                                        <span className="viz-row__unit">in³</span>
                                    </span>
                                </div>
                                <div className="viz-row">
                                    <span className="uppercase-label">Filled vol</span>
                                    <span className="mono-num viz-row__val">
                                        {filledVol != null ? filledVol.toFixed(0) : "—"}
                                        <span className="viz-row__unit">in³</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="viz-section">
                            <div className="uppercase-label viz-section__label">
                                Shortcuts
                            </div>
                            <div className="viz-shortcuts">
                                <div className="viz-shortcut">
                                    <KeyChip>1</KeyChip>
                                    <span>Optimise volume</span>
                                </div>
                                <div className="viz-shortcut">
                                    <KeyChip>2</KeyChip>
                                    <span>Optimise count</span>
                                </div>
                                <div className="viz-shortcut">
                                    <KeyChip>←</KeyChip>
                                    <span>Previous box</span>
                                </div>
                                <div className="viz-shortcut">
                                    <KeyChip>→</KeyChip>
                                    <span>Next box</span>
                                </div>
                                <div className="viz-shortcut">
                                    <KeyChip>Home</KeyChip>
                                    <span>First box</span>
                                </div>
                                <div className="viz-shortcut">
                                    <KeyChip>End</KeyChip>
                                    <span>Last box</span>
                                </div>
                                <div className="viz-shortcut">
                                    <KeyChip>R</KeyChip>
                                    <span>Reset</span>
                                </div>
                                <div className="viz-shortcut">
                                    <KeyChip>V</KeyChip>
                                    <span>Reset view</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </Rail>
            </div>

            <SkuPickerModal
                open={pickerForIdx !== null}
                onClose={() => setPickerForIdx(null)}
                onSelect={(sku) => {
                    if (pickerForIdx != null) applySku(pickerForIdx, sku);
                    setPickerForIdx(null);
                }}
            />
        </div>
    );
}

export default ManifestPage;
